// ANGEL FLIGHT — TouchControls: the in-game touch command layer (flat DOM).
//
// Touch-first finally means it: a weapon rack (slots 1-4 with honest
// locked/owned/active states matching the keyboard + the Armory), a PAUSE
// button, and the FLIR toggle — all reachable mid-mission on an iPad without a
// keyboard. Drawn as DOM siblings of the HUD (composited last, never graded).
// The root is pointer-events:none; only the buttons re-enable, so the canvas
// keeps receiving drag-to-aim everywhere else. Buttons sit OVER the canvas, so
// tapping one never moves the reticle or fires.
//
// Lifecycle mirrors HudOverlay: built once by App, bind(bus)/unbind() per
// mission (tracks weapon:switched to highlight the active slot), show()/hide().

import { WEAPONS, WEAPON_SLOTS } from '../config/weapons';
import type { TypedEmitter } from '../core/EventBus';
import type { WeaponId } from '../core/types';

const FONT = "ui-monospace, 'SFMono-Regular', Menlo, monospace";
const GREEN = '#7dff9a';
const GREEN_DIM = 'rgba(125,255,154,0.5)';
const GREEN_FAINT = 'rgba(125,255,154,0.25)';
const AMBER = '#ffd27a';
const WARN = '#ff7a4d';
const PANEL = 'rgba(8,14,11,0.6)';

/** Short rack code per weapon (the HUD chrome uses the same vocabulary). */
const WEAPON_CODES: Readonly<Record<WeaponId, string>> = Object.freeze({
  minigun: 'M134',
  autocannon: '30MM',
  railgun: 'RAIL',
  flamer: 'INCN',
});

/** True when the device can actually touch the buttons (iPad/phone/hybrid). */
function isTouchDevice(): boolean {
  return 'ontouchstart' in window || navigator.maxTouchPoints > 0;
}

export interface TouchControlsCallbacks {
  /** A weapon slot was tapped (only ever fired for OWNED weapons). */
  onWeapon(id: WeaponId): void;
  onPause(): void;
  onFlir(): void;
  /** The FLARE button was tapped (the sim still gates on the cooldown). */
  onFlare(): void;
}

interface SlotButton {
  id: WeaponId;
  el: HTMLButtonElement;
  code: HTMLDivElement;
  owned: boolean;
  fn: () => void;
}

export class TouchControls {
  private readonly root: HTMLDivElement;
  private readonly cb: TouchControlsCallbacks;
  private readonly slots: SlotButton[] = [];
  private readonly flirBtn: HTMLButtonElement;
  private readonly pauseBtn: HTMLButtonElement;
  private readonly flareBtn: HTMLButtonElement;
  private readonly flirFn: () => void;
  private readonly pauseFn: () => void;
  private readonly flareFn: () => void;
  private bus: TypedEmitter | null = null;
  private active: WeaponId = 'minigun';
  // FLARE button state: cooldown countdown (refreshed by an interval while
  // cooling) + the ids of flare-able rockets currently in flight (the button
  // lights red while any are inbound — the touch player's "press NOW" cue).
  private flareReadyAtMs = 0;
  private flareTick = 0;
  private readonly rocketsInbound = new Set<number>();

  constructor(parent: HTMLElement, cb: TouchControlsCallbacks) {
    this.cb = cb;
    this.root = document.createElement('div');
    Object.assign(this.root.style, {
      position: 'absolute',
      inset: '0',
      pointerEvents: 'none',
      font: `700 12px/1 ${FONT}`,
      letterSpacing: '0.1em',
      padding:
        'max(env(safe-area-inset-top), 18px) max(env(safe-area-inset-right), 22px) max(env(safe-area-inset-bottom), 18px) max(env(safe-area-inset-left), 22px)',
      boxSizing: 'border-box',
    } as CSSStyleDeclaration);

    // ── Weapon rack: bottom-right vertical column, thumb reach on iPad ──────
    const rack = document.createElement('div');
    Object.assign(rack.style, {
      position: 'absolute',
      right: 'max(env(safe-area-inset-right), 22px)',
      bottom: 'max(env(safe-area-inset-bottom), 18px)',
      display: 'flex',
      flexDirection: 'column',
      gap: '10px',
    } as CSSStyleDeclaration);

    WEAPON_SLOTS.forEach((id, i) => {
      const btn = this.makeButton();
      Object.assign(btn.style, {
        width: '64px',
        minHeight: '56px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '4px',
      } as CSSStyleDeclaration);
      const digit = document.createElement('div');
      digit.textContent = `${i + 1}`;
      Object.assign(digit.style, { font: `800 16px/1 ${FONT}` } as CSSStyleDeclaration);
      const code = document.createElement('div');
      code.textContent = WEAPON_CODES[id];
      Object.assign(code.style, { font: `700 10px/1 ${FONT}`, letterSpacing: '0.12em' } as CSSStyleDeclaration);
      btn.append(digit, code);
      btn.title = WEAPONS[id].name;
      const fn = (): void => {
        const slot = this.slots.find((s) => s.id === id);
        if (slot?.owned) this.cb.onWeapon(id);
      };
      btn.addEventListener('click', fn);
      rack.appendChild(btn);
      this.slots.push({ id, el: btn, code, owned: id === 'minigun', fn });
    });

    // ── Top-right command row: FLIR toggle + PAUSE ──────────────────────────
    // Sits BELOW the HUD's payout panel + combo meter (panel ≈ 100px tall +
    // the combo line at +104px — 150px clears both with breathing room).
    const cmd = document.createElement('div');
    Object.assign(cmd.style, {
      position: 'absolute',
      right: 'max(env(safe-area-inset-right), 22px)',
      top: 'calc(max(env(safe-area-inset-top), 18px) + 150px)',
      display: 'flex',
      gap: '10px',
    } as CSSStyleDeclaration);

    this.flirBtn = this.makeButton();
    this.flirBtn.textContent = 'FLIR';
    Object.assign(this.flirBtn.style, { minWidth: '64px', minHeight: '44px' } as CSSStyleDeclaration);
    this.flirFn = (): void => this.cb.onFlir();
    this.flirBtn.addEventListener('click', this.flirFn);

    this.pauseBtn = this.makeButton();
    this.pauseBtn.textContent = 'PAUSE';
    Object.assign(this.pauseBtn.style, { minWidth: '64px', minHeight: '44px' } as CSSStyleDeclaration);
    this.pauseFn = (): void => this.cb.onPause();
    this.pauseBtn.addEventListener('click', this.pauseFn);

    cmd.append(this.flirBtn, this.pauseBtn);

    // ── Bottom-left: FLARE countermeasure (the LEFT thumb's button, mirroring
    //    the weapon rack on the right — reachable while the right hand aims) ──
    this.flareBtn = this.makeButton();
    this.flareBtn.textContent = 'FLARES';
    Object.assign(this.flareBtn.style, {
      position: 'absolute',
      left: 'max(env(safe-area-inset-left), 22px)',
      bottom: 'max(env(safe-area-inset-bottom), 18px)',
      minWidth: '84px',
      minHeight: '56px',
      font: `800 14px/1 ${FONT}`,
    } as CSSStyleDeclaration);
    this.flareBtn.title = 'Countermeasure flares (key F) — decoys rockets in flight';
    this.flareFn = (): void => this.cb.onFlare();
    this.flareBtn.addEventListener('click', this.flareFn);

    this.root.append(rack, cmd, this.flareBtn);
    parent.appendChild(this.root);
    this.paintSlots();
    this.setFlir(false);
    this.paintFlare();
    this.hide();
  }

  private makeButton(): HTMLButtonElement {
    const b = document.createElement('button');
    Object.assign(b.style, {
      appearance: 'none',
      webkitAppearance: 'none',
      pointerEvents: 'auto',
      touchAction: 'manipulation',
      userSelect: 'none',
      webkitUserSelect: 'none',
      background: PANEL,
      border: `1px solid ${GREEN_FAINT}`,
      borderRadius: '10px',
      color: GREEN,
      font: `700 13px/1 ${FONT}`,
      letterSpacing: '0.12em',
      cursor: 'pointer',
      textShadow: '0 0 6px rgba(0,0,0,0.9)',
      padding: '8px',
    } as CSSStyleDeclaration);
    return b;
  }

  /** Repaint the rack from owned + active state (honest locks). */
  private paintSlots(): void {
    for (const s of this.slots) {
      const isActive = s.owned && s.id === this.active;
      s.el.style.opacity = s.owned ? '1' : '0.38';
      s.el.style.cursor = s.owned ? 'pointer' : 'default';
      s.el.style.borderColor = isActive ? AMBER : GREEN_FAINT;
      s.el.style.color = isActive ? AMBER : s.owned ? GREEN : GREEN_DIM;
      s.el.style.boxShadow = isActive ? '0 0 10px rgba(255,210,122,0.35)' : 'none';
      s.code.textContent = s.owned ? WEAPON_CODES[s.id] : 'LOCKED';
    }
  }

  /** Owned set for this mission — locked slots dim + read LOCKED. */
  setOwned(owned: readonly WeaponId[]): void {
    for (const s of this.slots) s.owned = owned.includes(s.id);
    this.paintSlots();
  }

  setActive(id: WeaponId): void {
    this.active = id;
    this.paintSlots();
  }

  /** FLIR button state (lit amber while thermal is live). */
  setFlir(on: boolean): void {
    this.flirBtn.style.color = on ? AMBER : GREEN;
    this.flirBtn.style.borderColor = on ? AMBER : GREEN_FAINT;
    this.flirBtn.style.boxShadow = on ? '0 0 10px rgba(255,210,122,0.35)' : 'none';
    this.flirBtn.textContent = on ? 'FLIR ON' : 'FLIR';
  }

  /** Repaint the FLARE button from threat + cooldown state. Priority: an
   *  inbound rocket reads RED ("press NOW"), a cooldown reads dim with a
   *  countdown, ready reads green. */
  private paintFlare(): void {
    const cdLeft = Math.max(0, this.flareReadyAtMs - performance.now());
    const inbound = this.rocketsInbound.size > 0;
    if (cdLeft > 0) {
      this.flareBtn.textContent = `FLARES ${Math.ceil(cdLeft / 1000)}`;
      this.flareBtn.style.color = GREEN_DIM;
      this.flareBtn.style.borderColor = GREEN_FAINT;
      this.flareBtn.style.boxShadow = 'none';
      this.flareBtn.style.opacity = '0.55';
    } else {
      this.flareBtn.textContent = 'FLARES';
      this.flareBtn.style.opacity = '1';
      this.flareBtn.style.color = inbound ? WARN : GREEN;
      this.flareBtn.style.borderColor = inbound ? WARN : GREEN_FAINT;
      this.flareBtn.style.boxShadow = inbound ? '0 0 16px rgba(255,122,77,0.7)' : 'none';
    }
  }

  /** While cooling down (or a rocket is inbound) refresh the countdown ~4×/s;
   *  stops itself once ready + clear. */
  private ensureFlareTick(): void {
    if (this.flareTick) return;
    this.flareTick = window.setInterval(() => {
      this.paintFlare();
      if (this.rocketsInbound.size === 0 && performance.now() >= this.flareReadyAtMs) {
        window.clearInterval(this.flareTick);
        this.flareTick = 0;
      }
    }, 250);
  }

  /** Track the live weapon so the rack highlight follows keys AND taps. */
  bind(bus: TypedEmitter): void {
    this.unbind();
    this.bus = bus;
    bus.on('weapon:switched', this.onSwitched);
    bus.on('flare:deployed', this.onFlareDeployed);
    bus.on('enemy:firedProjectile', this.onEnemyProjectile);
    bus.on('projectile:decoyed', this.onRocketGone);
    bus.on('projectile:hitGunner', this.onRocketGone);
    this.flareReadyAtMs = 0;
    this.rocketsInbound.clear();
    this.paintFlare();
  }

  private readonly onSwitched = (p: { weapon: WeaponId }): void => {
    this.setActive(p.weapon);
  };

  private readonly onFlareDeployed = (p: { cooldownMs: number }): void => {
    this.flareReadyAtMs = performance.now() + p.cooldownMs;
    this.paintFlare();
    this.ensureFlareTick();
  };

  /** A flare-able rocket left the tube — light the button red until that
   *  rocket resolves (decoyed or struck). Non-flareable projectiles never
   *  enter the set, so a bullet hit can't clear a rocket warning. */
  private readonly onEnemyProjectile = (p: { id: number; flareable: boolean }): void => {
    if (!p.flareable) return;
    this.rocketsInbound.add(p.id);
    this.paintFlare();
    this.ensureFlareTick();
  };

  private readonly onRocketGone = (p: { id: number }): void => {
    if (!this.rocketsInbound.delete(p.id)) return;
    this.paintFlare();
  };

  unbind(): void {
    if (this.flareTick) {
      window.clearInterval(this.flareTick);
      this.flareTick = 0;
    }
    this.rocketsInbound.clear();
    this.flareReadyAtMs = 0;
    if (!this.bus) return;
    this.bus.off('weapon:switched', this.onSwitched);
    this.bus.off('flare:deployed', this.onFlareDeployed);
    this.bus.off('enemy:firedProjectile', this.onEnemyProjectile);
    this.bus.off('projectile:decoyed', this.onRocketGone);
    this.bus.off('projectile:hitGunner', this.onRocketGone);
    this.bus = null;
  }

  /** TOUCH DEVICES ONLY: on a desktop the keyboard covers everything (1-4
   *  weapons, F flares, T FLIR, Esc pause), so the button layer would just be
   *  screen clutter — it stays hidden there. */
  show(): void {
    if (!isTouchDevice()) return;
    this.root.style.display = 'block';
  }
  hide(): void {
    this.root.style.display = 'none';
  }

  destroy(): void {
    this.unbind();
    for (const s of this.slots) s.el.removeEventListener('click', s.fn);
    this.flirBtn.removeEventListener('click', this.flirFn);
    this.pauseBtn.removeEventListener('click', this.pauseFn);
    this.flareBtn.removeEventListener('click', this.flareFn);
    this.root.remove();
  }
}
